import PropTypes from "prop-types";
import { useContext } from "react";
import { formatISO, parse } from "date-fns";

import { withALErrorBoundary } from "../../helpers/ErrorBoundary/ALErrorBoundary";
import { useCurrentFormatCurrency } from "../../hooks/usePrices";
import { roundNumber, cn } from "@helpers/general";
import { ShippingDelayContext } from "../../context/ShippingDelayProvider";
import { getShipByDate, getDaysUntilShipping } from "../../helpers/product";
import useDiscountsInfo from "../../hooks/useDiscountsInfo";

import DISCOUNT_TYPES from "../../constants/DiscountTypes";
import { HandleType } from "../../constants/HandleType";

import SideCartTotalTieredDiscount from "./SideCartTotalTieredDiscount";

function PriceSummary({ sideCartInfo, shippingInfo, isLuxe, showCheckoutCallout, eligibleAmount }) {
  const discountsInfo = useDiscountsInfo();
  const { deliveryDateFormatted, isShippingEstimationLoading, deliveryTimelinesFormatted } =
    useContext(ShippingDelayContext);

  const formatCurrency = useCurrentFormatCurrency();

  const siteWideDiscountItemCount = sideCartInfo.nonBannedItemQuantity;
  const hasFreeShipping =
    (shippingInfo?.freeShippingThreshold &&
      sideCartInfo.finalAmount > shippingInfo.freeShippingThreshold) ||
    shippingInfo?.freeShippingThreshold === 0;

  const preOrderItems = sideCartInfo.lines.filter((lineItem) =>
    lineItem.attributes?.find(({ key, value }) => key === "_pre_order" && value === "true")
  );

  let deliveryDateToDisplay = `Estimated Delivery by: ${deliveryDateFormatted}`;
  let hasPreorder = false;
  // Show preorder date only if there is one item in the cart and that item is a preorder
  if (
    sideCartInfo.lines.filter(
      (l) =>
        ![HandleType.SUBSCRIPTION, HandleType.ORDER_PROTECTION].includes(
          l?.variant?.product?.handle
        )
    ).length === 1 &&
    preOrderItems.length === 1
  ) {
    const shipsByDateAttr =
      preOrderItems[0].attributes?.find(({ key }) => key === "_ships_by_pre_order")?.value || "";
    const shipsByDate = parse(shipsByDateAttr, "MMM d, yyyy", new Date());
    const daysUntilShipping = shipsByDate ? getDaysUntilShipping(shipsByDate) : 0;
    if (daysUntilShipping > 7) {
      hasPreorder = true;
      deliveryDateToDisplay = `Ships by: ${getShipByDate(
        formatISO(shipsByDate, { representation: "date" }),
        "MMM do"
      )}`;
    }
  }

  const filteredLines = sideCartInfo.lines.filter((li) => li.variant.sku !== "L-0001");
  let mtoTimeline = null;
  const hasOnlyMto =
    filteredLines.length > 0 &&
    filteredLines.every((item) =>
      item.attributes.some((attr) => attr.key === "_mto" && attr.value === "true")
    );

  if (hasOnlyMto) {
    const firstMtoItem = filteredLines.find((item) =>
      item.attributes.some((attr) => attr.key === "_mto" && attr.value === "true")
    );

    const timelineAttr = firstMtoItem?.attributes.find((attr) => attr.key === "_mto_timeline");
    mtoTimeline = timelineAttr?.value || null;
  }

  if (preOrderItems.length > 1) {
    deliveryDateToDisplay = "";
  }

  const tierDiscountQuantity = discountsInfo.find(
    ({ type }) => type === DISCOUNT_TYPES.QUANTITY_PERCENT
  );
  const tierDiscountAmount = discountsInfo.find(
    ({ type }) => type === DISCOUNT_TYPES.AMOUNT_PERCENT
  );

  let discountCodeText = "";

  if (!!tierDiscountQuantity && tierDiscountQuantity.thresholdConditionQuantity.length > 0) {
    const firstTierQuantityThreshold = tierDiscountQuantity.thresholdConditionQuantity[0];
    const lastTierQuantityThreshold =
      tierDiscountQuantity.thresholdConditionQuantity[
        tierDiscountQuantity.thresholdConditionQuantity.length - 1
      ];
    const lastTierDiscountPercentage =
      tierDiscountQuantity.thresholdResultPercentage[
        tierDiscountQuantity.thresholdResultPercentage.length - 1
      ];

    if (
      siteWideDiscountItemCount < lastTierQuantityThreshold &&
      siteWideDiscountItemCount >= firstTierQuantityThreshold
    ) {
      let appliedDiscountIndex = 0;
      for (let i = 0; i < tierDiscountQuantity.thresholdConditionQuantity.length; i += 1) {
        if (siteWideDiscountItemCount >= tierDiscountQuantity.thresholdConditionQuantity[i]) {
          appliedDiscountIndex = i;
        }
      }
      discountCodeText = `${tierDiscountQuantity.thresholdResultPercentage[appliedDiscountIndex]}% Off`;
    }
    if (siteWideDiscountItemCount >= lastTierQuantityThreshold) {
      discountCodeText = `${lastTierDiscountPercentage}% Off`;
    }
  } else if (!!tierDiscountAmount && tierDiscountAmount.thresholdConditionAmount.length > 0) {
    const thresholdConditionAmount = tierDiscountAmount.thresholdConditionAmount
      .map((v) => Number(v))
      .reverse();
    const appliedDiscountPercentIndex =
      thresholdConditionAmount.length -
      thresholdConditionAmount.findIndex((amount) => amount <= eligibleAmount) -
      1;
    const appliedDiscountPercent =
      appliedDiscountPercentIndex >= 0
        ? tierDiscountAmount.thresholdResultPercentage[appliedDiscountPercentIndex]
        : 0;
    if (appliedDiscountPercent > 0) {
      discountCodeText = `${appliedDiscountPercent}% Off`;
    }
  }

  const hasBuyMoreSaveMoreDiscount =
    (isLuxe && (sideCartInfo.discountTieredOfferLuxe || 0) > 0) ||
    (!isLuxe && (sideCartInfo.discountTieredOffer || 0) > 0);

  const buyMoreSaveMoreAmount = isLuxe
    ? sideCartInfo.discountTieredOfferLuxe
    : sideCartInfo.discountTieredOffer;
  const estimatedTotal = roundNumber(
    isLuxe ? sideCartInfo.finalAmountLux : sideCartInfo.finalAmount
  );

  const discountsGroup = (
    <>
      {/* AL Luxe */}
      {isLuxe && sideCartInfo.discountPercentOffLuxe > 0 && (
        <p className="typo-body flex justify-between text-secondary">
          <span className="flex mr-[5px]">
            AL Luxe Discount:&nbsp;<b>5% Off</b>&nbsp;Solid Gold
          </span>
          <span>{`- ${formatCurrency(roundNumber(sideCartInfo.discountPercentOffLuxe))}`}</span>
        </p>
      )}

      {/* Spend More, Save More */}
      {hasBuyMoreSaveMoreDiscount && (
        <p
          className="typo-body flex justify-between text-secondary"
          data-testid="sidecart_total_content_discount"
        >
          <span>
            {isLuxe ? "AL Luxe Discount:" : "Spend More, Save More:"}&nbsp;<b>{discountCodeText}</b>
          </span>
          <span>{`- ${formatCurrency(roundNumber(buyMoreSaveMoreAmount))}`}</span>
        </p>
      )}

      {/* Last Chance Discount */}
      {sideCartInfo.discountCompareAtPrice > 0 && (
        <p className="typo-body flex justify-between text-secondary">
          <span className="mr-[5px]">Other Discounts</span>
          <span>{`- ${formatCurrency(roundNumber(sideCartInfo.discountCompareAtPrice))}`}</span>
        </p>
      )}

      {/* Guest Percent off first order */}
      {!isLuxe && sideCartInfo.discountPercentOff > 0 && (
        <p className="typo-body flex justify-between">
          <span className="mr-[5px]">
            <b>{discountsInfo.find((d) => d.type === "percent_off")?.value}% Off</b>
            &nbsp;first order
          </span>
          <span className="text-secondary">
            {`- ${formatCurrency(roundNumber(sideCartInfo.discountPercentOff))}`}
          </span>
        </p>
      )}

      {/* Free gift Discount */}
      {sideCartInfo.discountFreeGift > 0 && (
        <p className="typo-body flex justify-between text-secondary">
          <span className="mr-[5px]">Free Gift</span>
          <span>{`- ${formatCurrency(roundNumber(sideCartInfo.discountFreeGift))}`}</span>
        </p>
      )}

      {/* Shipping */}
      {!hasFreeShipping && (
        <p className="typo-body flex justify-between">
          <span>Shipping</span>
          <span>{formatCurrency(shippingInfo?.shippingPrice)}</span>
        </p>
      )}
    </>
  );

  return (
    <div
      className={cn(
        "flex flex-col p-[20px] pb-0 mobile:px-[15px] mobile:pb-0 gap-[6px] mobile:gap-[5px] bg-light-grey",
        showCheckoutCallout ? "sidecart_total_extra_padding" : ""
      )}
    >
      {/* Subtotal */}
      <p className="typo-body flex justify-between">
        <span>Subtotal</span>
        <span>{formatCurrency(sideCartInfo.fullAmount)}</span>
      </p>
      {discountsGroup}

      {/* Luxe Discount */}

      <div className="typo-body flex justify-between font-bold">
        <span className="mr-[5px]">Total</span>

        <SideCartTotalTieredDiscount
          estimatedTotal={estimatedTotal}
          fullAmount={sideCartInfo.fullAmount}
          hasFreeShipping={hasFreeShipping}
          shippingPrice={shippingInfo?.shippingPrice}
        />
      </div>

      <p className="typo-body mt-[-6px] mobile:mt-[-5px]">
        {mtoTimeline
          ? `Delivery within ${mtoTimeline}`
          : deliveryDateToDisplay &&
            deliveryTimelinesFormatted &&
            !isShippingEstimationLoading &&
            (hasPreorder
              ? deliveryDateToDisplay
              : `${
                  hasFreeShipping ? "Free " : "Estimated "
                } Delivery by ${deliveryTimelinesFormatted}`)}
      </p>
      {shippingInfo?.isStandardShipping && (
        <p className="typo-micro mt-[-6px] mobile:mt-[-5px] pb-[20px] mobile:pb-[15px]">
          *Express shipping available at checkout
        </p>
      )}
    </div>
  );
}

PriceSummary.propTypes = {
  sideCartInfo: PropTypes.shape({
    lines: PropTypes.array,
    fullAmount: PropTypes.number,
    finalAmount: PropTypes.number,
    nonBannedItemQuantity: PropTypes.number,
    discountLuxe: PropTypes.number,
    discountTieredOffer: PropTypes.number,
    discountTieredOfferLuxe: PropTypes.number,
    discountCompareAtPrice: PropTypes.number,
    discountPercentOff: PropTypes.number,
    discountPercentOffLuxe: PropTypes.number,
  }).isRequired,
  shippingInfo: PropTypes.shape({
    freeShippingThreshold: PropTypes.number,
    shippingPrice: PropTypes.number,
  }).isRequired,
  isLuxe: PropTypes.bool,
};

export default withALErrorBoundary({
  name: "PriceSummary",
  priority: "P1",
})(PriceSummary);
